import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { uploadAxiosInstance } from '../../config/uploadAxios';
import { useAuth } from '../../hooks/useAuth';
import { useUnitats } from '../../hooks/useUnitats';
import { extractErrorMessage } from '../../utils/errorHandling';
import { LoadingGif, LoadingMessage } from '../Shared/CommonStyles';
import {
	Button,
	ErrorMessage,
	FormContainer,
	FormGroup,
	Input,
	Label,
	Select,
} from '../Shared/FormStyles';

interface AlbumFormData {
	name: string;
	albumFile: unknown;
	schoolYear?: string | null;
	customSchoolYear?: string;
	unitat: string;
}

const albumSchema = yup.object().shape({
	name: yup
		.string()
		.required('Aquest camp és obligatori')
		.max(160, 'El nom no pot tenir més de 160 caràcters'),
	albumFile: yup.mixed().required('Aquest camp és obligatori'),
	schoolYear: yup.string().nullable(),
	customSchoolYear: yup.string().when('schoolYear', {
		is: 'custom',
		then: (schema) =>
			schema
				.required('Introdueix un any')
				.matches(/^\d{4}-\d{4}$/, 'Format incorrecte (ex: 1990-1991)'),
		otherwise: (schema) => schema.notRequired(),
	}),
	unitat: yup.string().required('Has de seleccionar una unitat'),
});

const generateSchoolYears = () => {
	const currentYear = new Date().getFullYear();
	const currentMonth = new Date().getMonth(); // 0-11

	const startYear = currentMonth >= 8 ? currentYear : currentYear - 1;
	const years = [];

	for (let y = startYear; y >= 2000; y--) {
		years.push(`${y}-${y + 1}`);
	}
	return years;
};

const AlbumForm = () => {
	const { user } = useAuth();
	const { category } = useParams<{ category: string }>();
	const paramUnitat = category || '';
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const {
		unitats,
		isFetchingUnitats,
		fetchUnitatsError,
	} = useUnitats();

	const [authError, setAuthError] = useState<string | null>(null);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const schoolYears = generateSchoolYears();

	const isAuthenticated = !!user;

	// Authorization guard: determine if the user can create albums
	const canCreateAlbum = useMemo(() => {
		if (!user) return false;
		// Admin, Agrupament, Caps can create in any category
		if (
			user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') // trusted curators: any category
		) {
			return true;
		}
		// Unitat can only create in their own unitat category
		if (
			user.roles.includes('Unitat') &&
			user.unitat === paramUnitat
		) {
			return true;
		}
		// Excaps, Familiar, and others cannot create albums
		return false;
	}, [user, paramUnitat]);

	const filteredUnitats = useMemo(() => {
		if (!unitats || !user) return [];
		if (
			user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps')
		) {
			return unitats;
		}
		if (user.unitat) {
			return unitats.filter(
				(u) => u.abreviacio.toLowerCase() === user.unitat
			);
		}
		return [];
	}, [unitats, user]);

	// Use standard useMutation instead of useSimpleMutation
	const postMutation = useMutation({
		mutationFn: async (data: FormData) => {
			const response = await uploadAxiosInstance.post(
				'/api/galeria/albums',
				data,
				{
					onUploadProgress: (progressEvent) => {
						const progress = Math.round(
							(progressEvent.loaded * 100) /
								(progressEvent.total ?? 1)
						);
						setUploadProgress(progress);
					},
				}
			);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['albums', paramUnitat],
			});
			queryClient.invalidateQueries({ queryKey: ['albums', 'tots'] });
			navigate(`/galeria/${paramUnitat}`);
		},
	});

	const {
		register,
		handleSubmit,
		getValues,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(albumSchema),
		defaultValues: {
			schoolYear: schoolYears[0],
			unitat: paramUnitat,
		},
	});

	const selectedSchoolYear = watch('schoolYear');

	const onSubmit = async (formData: AlbumFormData) => {
		const files = getValues('albumFile');
		let fileToUpload: File | null = null;

		if (files instanceof FileList && files.length > 0) {
			fileToUpload = files[0];
		}

		if (fileToUpload && fileToUpload.size > 2 * 1024 * 1024 * 1024) {
			setAuthError(
				"L'arxiu és massa gran (>2GB). Si us plau, puja un arxiu més petit i afegeix la resta d'imatges editant l'àlbum."
			);
			return;
		}

		const submissionData = new FormData();
		submissionData.append('name', formData.name);
		submissionData.append('unitat', formData.unitat);

		let finalSchoolYear = formData.schoolYear;
		if (finalSchoolYear === 'custom') {
			finalSchoolYear = formData.customSchoolYear || '';
		}
		if (finalSchoolYear) {
			submissionData.append('schoolYear', finalSchoolYear);
		}

		if (fileToUpload) {
			submissionData.append('albumFile', fileToUpload);
		}

		postMutation.mutate(submissionData);
	};

	useEffect(() => {
		if (!user) {
			setAuthError('No estàs autenticat');
		} else if (!isAuthenticated) {
			setAuthError('No tens permís');
		} else {
			setAuthError(null);
		}
	}, [isAuthenticated, setAuthError]);

	// Authorization guard: redirect if user doesn't have permission to create albums
	if (!canCreateAlbum && user && !isFetchingUnitats) {
		return <Navigate to="/galeria" replace />;
	}

	const loading = isFetchingUnitats;
	const error = authError || fetchUnitatsError || postMutation.error;

	return loading ? (
		<LoadingMessage>
			<LoadingGif size="large" />
		</LoadingMessage>
	) : error ? (
		<ErrorMessage>{extractErrorMessage(error)}</ErrorMessage>
	) : (
		<FormContainer onSubmit={handleSubmit(onSubmit)}>
			<FormGroup>
				<Label htmlFor="name">Nom de l&apos;àlbum</Label>
				<Input id="name" {...register('name')} />
				{errors.name && (
					<ErrorMessage>
						{errors.name.message?.toString()}
					</ErrorMessage>
				)}
			</FormGroup>

			<FormGroup>
				<Label htmlFor="unitat">Unitat</Label>
				<Select id="unitat" {...register('unitat')}>
					{(user?.roles.includes('Admin') ||
						user?.roles.includes('Agrupament')) && (
						<option value="agrupament">Agrupament</option>
					)}
					{filteredUnitats.map((unitat) => (
						<option
							key={unitat._id}
							value={unitat.abreviacio.toLowerCase()}
						>
							{unitat.nom}
						</option>
					))}
				</Select>
				{errors.unitat && (
					<ErrorMessage>
						{errors.unitat.message?.toString()}
					</ErrorMessage>
				)}
			</FormGroup>

			<FormGroup>
				<Label htmlFor="schoolYear">Curs</Label>
				<Select id="schoolYear" {...register('schoolYear')}>
					{schoolYears.map((year) => (
						<option key={year} value={year}>
							{year}
						</option>
					))}
					<option value="">Varis / Desconegut</option>
					<option value="custom">Més antic...</option>
				</Select>
				{errors.schoolYear && (
					<ErrorMessage>
						{errors.schoolYear.message?.toString()}
					</ErrorMessage>
				)}
			</FormGroup>

			{selectedSchoolYear === 'custom' && (
				<FormGroup>
					<Label htmlFor="customSchoolYear">Escriu el curs</Label>
					<Input
						id="customSchoolYear"
						placeholder="1990-1991"
						{...register('customSchoolYear')}
					/>
					{errors.customSchoolYear && (
						<ErrorMessage>
							{errors.customSchoolYear.message?.toString()}
						</ErrorMessage>
					)}
				</FormGroup>
			)}

			<FormGroup>
				<Label htmlFor="albumFile">
					Fitxer de l&apos;àlbum (format .zip)
				</Label>
				<Input
					id="albumFile"
					type="file"
					accept=".zip"
					{...register('albumFile')}
				/>
				{errors.albumFile && (
					<ErrorMessage>
						{errors.albumFile.message?.toString()}
					</ErrorMessage>
				)}
			</FormGroup>

			<Button type="submit" disabled={postMutation.isPending}>
				{postMutation.isPending
					? `Pujant... ${uploadProgress}%`
					: 'Crear Àlbum'}
			</Button>
		</FormContainer>
	);
};

export default AlbumForm;